import { tv } from "@utils/tw"
import { ReactNode, useRef } from "react"
import { DonationSelector } from "./DonationSelector"
import { Link } from "eddev/routing"
import { IconChevronRight } from "@components/ui/Icon"
import { trackEvent } from "@features/tracking/tracking"

type Props = {
  title: ReactNode
  description: ReactNode
  label: ReactNode
  ferveHash: string
  amounts?: (number | null)[]
  isGiftCard?: boolean
  altDonationLink?: {
    title: string
    url: string
    target: string
  }
  readMoreLink?: {
    title: string
    url: string
    target: string
  }
}

export function DonatePanel(props: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const styles = createStyles({
    isGiftCard: props.isGiftCard,
  })

  return (
    <div data-mixpanel-area={props.isGiftCard ? "Donate Panel" : "Gift Card Panel"} ref={ref}>
      <div className={styles.base()}>
        <div className={styles.inner()}>
          <div className={styles.info()}>
            <h3 className={styles.title()}>{props.title}</h3>
            <div className={styles.description()}>{props.description}</div>
          </div>
          <div className={styles.selection()}>
            <div className={styles.label()}>{props.label}</div>
            <DonationSelector
              ferveHash={props.ferveHash}
              amounts={props.amounts ?? []}
              altDonationLink={props.altDonationLink}
              buttonLabel={props.isGiftCard ? "Add to Cart" : "Donate"}
              onAddToCart={(amount, isOther) => {
                trackEvent(
                  props.isGiftCard ? "Add Gift Card To Cart" : "Add Donation to Cart",
                  {
                    amount: amount,
                    IsOther: isOther,
                  },
                  ref.current,
                )
              }}
            />
            {props.readMoreLink && (
              <div className="flex items-center justify-end type-ui-s">
                <Link
                  href={props.readMoreLink.url}
                  target={props.readMoreLink.target}
                  className="flex items-center gap-1"
                >
                  {props.readMoreLink.title ?? "Read More"}
                  <IconChevronRight />
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const createStyles = tv({
  base: "bg-orange theme-light text-fg p-6 lg:p-5 w-full @container",
  slots: {
    inner: "flex flex-col gap-4 @2xl:flex-row @2xl:gap-5",
    info: "flex flex-col gap-3 @2xl:w-[40%] justify-between",
    selection: "flex flex-col gap-5 @2xl:flex-1 @2xl:justify-between",
    title: "type-title-l mb-[0.3em]",
    description: "type-body-m",
    label: "type-label-caps",
  },
  variants: {
    isGiftCard: {
      true: {
        base: "theme-dark bg-black text-fg is-gift-card",
      },
    },
  },
})
